import { getPeriodStartEnd } from '@dolfin/utils/dates'
import { Enums, Tables } from '../db_types'
import { aggregateCreditedMetricValues } from '../domains/credited_consumers'
import Targets from '../domains/targets'
import { Diagram } from '../diagram'
import { DolfinExecution } from '../execution'
import DolfinFlowExecutor from '../flow-executor/base'
import DolfinLoggerFactory from '../logger/factory'
import LogicEvaluator from '../logic-evaluator'
import type { NodeError, NodeFormDataMap, NodeId, NodeProcessOutput, NodeWarning } from '../types'
import { MetricRef, NodeIdExcludingPlaceholder, TargetRef, TypedNode, TypedNodeData } from './types'

/**
 * Abstract base class for all Dolfin diagram nodes.
 *
 * @typeParam TExecutor - The flow executor type (e.g., DolfinCompensationRulesFlowExecutor)
 * @typeParam TNodeId - The node type ID for typed formData access (e.g., 'commission', 'bonus')
 *
 * @example
 * ```typescript
 * // With typed formData:
 * class CommissionNode extends DolfinNode<DolfinCompensationRulesFlowExecutor, 'commission'> {
 *   async process() {
 *     // this.formData is now typed as CommissionFormData
 *     if (this.formData.type === 'straight') { ... }
 *   }
 * }
 *
 * // Without typed formData (legacy):
 * class SomeNode extends DolfinNode<DolfinFlowExecutor> {
 *   // this.formData is Record<string, any>
 * }
 * ```
 */
export abstract class DolfinNode<
  TExecutor extends DolfinFlowExecutor,
  TNodeId extends NodeId = NodeId
> {
  protected weightedTargetAchievement = 0
  private userFields?: { [index: string]: unknown }
  private metricValues: { [index: string]: number } = {}
  private targetAchievements: { [index: string]: number } = {}

  constructor(
    protected node: TypedNode<TNodeId>,
    protected flowExecutor: TExecutor,
    protected logger = DolfinLoggerFactory.create()
  ) {}

  /**
   * Type-safe accessor for node formData.
   * When the class is typed with a specific TNodeId, this returns the correctly typed formData.
   *
   * @example
   * ```typescript
   * // In CommissionNode extends DolfinNode<..., 'commission'>:
   * this.formData.type // 'straight' | 'tiered' | 'piecework' | 'field'
   * this.formData.percentage // number | undefined
   * ```
   */
  protected get formData(): TNodeId extends keyof NodeFormDataMap
    ? NodeFormDataMap[TNodeId]
    : never {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.node.data as any).formData
  }

  static validate(
    _node: TypedNode<NodeIdExcludingPlaceholder>,

    _diagram: Diagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const errors: NodeError[] = []
    const warnings: NodeWarning[] = []

    return {
      errors,
      warnings
    }
  }

  protected static usesWeightedTargetAchievement(filters: unknown): boolean {
    if (Array.isArray(filters)) {
      return filters.some(filter => this.usesWeightedTargetAchievement(filter))
    }

    if (!filters || typeof filters !== 'object') {
      return false
    }

    const entries = Object.entries(filters)
    return entries.some(([key, value]) =>
      key === 'var' && value === 'weighted_target_achievement'
        ? true
        : this.usesWeightedTargetAchievement(value)
    )
  }

  protected static validateWeightedTargetAchievement(
    node: TypedNode<NodeIdExcludingPlaceholder>,
    filters: unknown
  ): NodeError[] {
    if (!this.usesWeightedTargetAchievement(filters)) {
      return []
    }

    const formData = node.data.formData as {
      targets?: Array<{ id?: unknown; weight?: unknown }>
      use_target_weights?: boolean
    }
    const targets = formData.targets ?? []
    const errors: NodeError[] = []

    if (targets.length < 2) {
      errors.push({
        message: 'Weighted target achievement requires at least two targets.',
        node
      })
      return errors
    }

    if (targets.some(target => typeof target.id !== 'number')) {
      errors.push({
        message: 'Weighted target achievement requires every target to be selected.',
        node
      })
    }

    if (!formData.use_target_weights) {
      return errors
    }

    if (targets.some(target => typeof target.weight !== 'number')) {
      errors.push({
        message: 'Weighted target achievement requires a weight for each target.',
        node
      })
      return errors
    }

    const weights = targets.map(target => target.weight as number)
    if (weights.some(weight => !Number.isFinite(weight) || weight < 0)) {
      errors.push({
        message: 'Weighted target achievement weights must be zero or greater.',
        node
      })
      return errors
    }

    const totalWeight = weights.reduce((total, weight) => total + weight, 0)
    if (Math.abs(totalWeight - 1) > 0.0001) {
      errors.push({
        message: 'Weighted target achievement weights must add up to 100%.',
        node
      })
    }

    return errors
  }

  protected getProfileId() {
    return this.flowExecutor.period ? this.flowExecutor.profileId! : this.getRecordProfileId()!
  }

  protected hasLongTermBalancing() {
    const executor = this.flowExecutor as DolfinFlowExecutor & {
      compensationRule?: Tables<'compensation_rules'>
    }
    return !!this.flowExecutor.period && !!executor.compensationRule?.long_term_balancing_period
  }

  protected shouldApplyLongTermOnlyIfHigher() {
    const executor = this.flowExecutor as DolfinFlowExecutor & {
      compensationRule?: Tables<'compensation_rules'>
    }
    return !!executor.compensationRule?.long_term_balancing_only_if_higher
  }

  protected getComputationPeriod(forceCurrentPeriod = false): { start: string; end: string } {
    if (!this.flowExecutor.period) {
      throw new Error('Computation period is only available for period rules')
    }

    if (!forceCurrentPeriod && this.hasLongTermBalancing()) {
      return this.getBalancingPeriod()
    }

    return {
      start: this.flowExecutor.period.start,
      end: this.flowExecutor.period.end
    }
  }

  protected getBalancingPeriod(): { start: string; end: string } {
    if (!this.flowExecutor.period) {
      throw new Error('Balancing period is only available for period rules')
    }

    const executor = this.flowExecutor as DolfinFlowExecutor & {
      compensationRule?: Tables<'compensation_rules'>
    }
    const period = executor.compensationRule?.long_term_balancing_period

    if (!period) {
      throw new Error('No balancing period found')
    }

    return {
      start: getPeriodStartEnd(period, new Date(this.flowExecutor.period.start)).start,
      end: this.flowExecutor.period.end
    }
  }

  protected async getPreviousPayoutsForLongTermBalancing(profileId = this.flowExecutor.profileId!) {
    if (!this.flowExecutor.period) {
      return []
    }

    const executor = this.flowExecutor as DolfinFlowExecutor & {
      compensationRule?: Tables<'compensation_rules'>
    }
    const compensationRule = executor.compensationRule

    if (!compensationRule?.id || !this.hasLongTermBalancing()) {
      return []
    }

    const getPreviousPayouts = this.flowExecutor.getPreviousPayouts
    if (getPreviousPayouts) {
      return getPreviousPayouts(
        profileId,
        this.flowExecutor.period.start,
        compensationRule.id,
        this.getBalancingPeriod().start
      )
    }

    return this.flowExecutor.rpcClient.getPreviousPayouts(
      profileId,
      this.flowExecutor.period.start,
      compensationRule.id,
      this.getBalancingPeriod().start
    )
  }

  protected getPayoutValueForLongTermBalancing(
    payout: Pick<Tables<'payouts'>, 'value' | 'original_value'>
  ) {
    return payout.original_value ?? payout.value ?? 0
  }

  /**
   * Unstatemented negative payouts are informative only; once a payout is
   * attached to a statement, its signed value affects later balancing.
   */
  protected isPayoutEligibleForLongTermBalancing(
    payout: Pick<Tables<'payouts'>, 'value' | 'original_value' | 'statement_id'>
  ) {
    const value = this.getPayoutValueForLongTermBalancing(payout)
    const isStatemented = payout.statement_id !== null && payout.statement_id !== undefined

    return value >= 0 || isStatemented
  }

  protected sumPreviousPayoutsForLongTermBalancing(
    payouts: Pick<Tables<'payouts'>, 'value' | 'original_value' | 'statement_id'>[]
  ) {
    return payouts.reduce(
      (total, payout) =>
        this.isPayoutEligibleForLongTermBalancing(payout)
          ? total + this.getPayoutValueForLongTermBalancing(payout)
          : total,
      0
    )
  }

  protected evaluateLogic(filters: Record<string, unknown>) {
    const evaluator = new LogicEvaluator(
      this.flowExecutor.source,
      this.flowExecutor.customFields ?? {}
    )

    if (this.flowExecutor.period) {
      console.info(JSON.stringify(filters))
      console.info(this.metricValues, this.targetAchievements, this.userFields)

      const parsedData = evaluator.getParsedPeriodData(
        filters,
        {
          ...this.metricValues,
          ...this.targetAchievements,
          compensation_value: this.flowExecutor.execution.getVariable('compensation_value') || 0,
          weighted_target_achievement: this.weightedTargetAchievement
        },
        this.userFields,
        { start: this.flowExecutor.period!.start }
      )

      return !!evaluator.evaluate(filters, parsedData)
    } else {
      const parsedItemData = {
        ...evaluator.getParsedItemData(
          filters,
          this.flowExecutor.recordData!,
          undefined,
          this.userFields
        ),
        ...this.targetAchievements,
        compensation_value: this.flowExecutor.execution.getVariable('compensation_value') || 0,
        weighted_target_achievement: this.weightedTargetAchievement
      }

      console.info(JSON.stringify(filters))
      console.info(parsedItemData)
      return !!evaluator.evaluate(filters, parsedItemData)
    }
  }

  protected async loadUserFields(filters: Record<string, unknown>) {
    const evaluator = new LogicEvaluator(
      this.flowExecutor.source,
      this.flowExecutor.customFields ?? {}
    )

    if (evaluator.hasUserFields(filters)) {
      const userId = !this.flowExecutor.period
        ? this.getRecordProfileId()
        : this.flowExecutor.profileId

      const accrualDate = !this.flowExecutor.period
        ? (this.flowExecutor.recordData!.getFieldValue(
            this.flowExecutor.dateField,
            this.flowExecutor.customFields
          ) as string)
        : this.flowExecutor.period.end

      const arrayCopy = [
        ...((await this.flowExecutor.getUserFields?.(
          this.flowExecutor.organizationId,
          userId || ''
        )) ?? [])
      ]
      arrayCopy.sort((a, b) => {
        const dateA = new Date(a.from_date)
        const dateB = new Date(b.from_date)
        return dateB.getTime() - dateA.getTime() // Sort descending by from_date
      })
      const fields = arrayCopy.find(f => f.from_date <= accrualDate)

      this.userFields = { id: userId, type: fields?.type }
    }
  }

  public getMetricIds(filters: Record<string, unknown>) {
    if (!this.flowExecutor.period) return []
    const evaluator = new LogicEvaluator(this.flowExecutor.source, {})
    return evaluator.getMetricIds(filters)
  }

  protected async loadMetricValues(
    metricIds: number[],
    periodStart?: string,
    teamIds?: number[],
    periodEnd?: string,
    sourceId?: number
  ) {
    if (!this.flowExecutor.period && !periodStart && !periodEnd) return
    if (teamIds) {
      const values = await this.flowExecutor.rpcClient.getMetricsValues(
        metricIds,
        periodStart ?? this.flowExecutor.period!.start,
        periodEnd ?? this.flowExecutor.period!.end,
        this.flowExecutor.organizationId,
        undefined,
        teamIds,
        sourceId
      )
      for (const metricId of metricIds) {
        const key = [
          `metric_${metricId}`,
          periodStart,
          periodEnd,
          teamIds ? [...teamIds].sort() : undefined,
          sourceId
        ]
          .filter(Boolean)
          .join('_')
        const value = values.find(v => v.metric_id === metricId)?.aggregated_value ?? 0
        this.metricValues[key] ??= value
        this.capturePeriodMetricInput({ id: metricId }, value, periodStart, periodEnd)
      }
    } else {
      for (const metricId of metricIds) {
        const key = [`metric_${metricId}`, periodStart, periodEnd, sourceId]
          .filter(Boolean)
          .join('_')
        if (this.metricValues[key] !== undefined) continue
        const metricValue = (
          await this.flowExecutor.rpcClient.getMetricsValues(
            [metricId],
            periodStart ?? this.flowExecutor.period!.start,
            periodEnd ?? this.flowExecutor.period!.end,
            this.flowExecutor.organizationId,
            [!this.flowExecutor.period ? this.getRecordProfileId()! : this.flowExecutor.profileId!],
            undefined,
            sourceId
          )
        )[0]?.aggregated_value

        const value = metricValue ?? 0
        this.metricValues[key] ??= value
        this.capturePeriodMetricInput({ id: metricId }, value, periodStart, periodEnd)
      }
    }
  }

  protected async getMetricReferenceValue(
    metricRef: MetricRef,
    periodStart: string,
    periodEnd: string,
    sourceId?: number,
    scope?: {
      creditedTeamIds?: number[]
      profileIds?: string[] | null
      teamIds?: number[]
      targetCurrency?: string
      useTargetCreditedMetricAggregation?: boolean
    }
  ): Promise<number> {
    const profileId = this.getProfileId()
    const captureMetric = (value: number) =>
      this.capturePeriodMetricInput(metricRef, value, periodStart, periodEnd, scope?.targetCurrency)

    if (metricRef.credit_setup_id == null) {
      const directProfileIds =
        scope?.profileIds !== undefined
          ? (scope.profileIds ?? undefined)
          : profileId
            ? [profileId]
            : undefined
      const values =
        scope === undefined
          ? sourceId === undefined
            ? await this.flowExecutor.rpcClient.getMetricsValues(
                [metricRef.id],
                periodStart,
                periodEnd,
                this.flowExecutor.organizationId,
                directProfileIds
              )
            : await this.flowExecutor.rpcClient.getMetricsValues(
                [metricRef.id],
                periodStart,
                periodEnd,
                this.flowExecutor.organizationId,
                directProfileIds,
                undefined,
                sourceId
              )
          : await this.flowExecutor.rpcClient.getMetricsValues(
              [metricRef.id],
              periodStart,
              periodEnd,
              this.flowExecutor.organizationId,
              directProfileIds,
              scope.teamIds,
              sourceId,
              undefined,
              undefined,
              undefined,
              undefined,
              scope.targetCurrency
            )

      const value = values[0]?.aggregated_value ?? 0
      captureMetric(value)
      return value
    }

    if (!profileId) {
      captureMetric(0)
      return 0
    }

    const membershipTeamIds = scope?.creditedTeamIds?.length ? scope.creditedTeamIds : undefined
    const memberships = await this.flowExecutor.getCreditedTeamMembershipsInPeriod({
      organizationId: this.flowExecutor.organizationId,
      start: periodStart,
      end: periodEnd,
      profileId,
      teamIds: membershipTeamIds
    })
    const teamMemberships = memberships.map(membership => ({
      teamId: membership.teamId,
      effectiveFrom: membership.activeFrom,
      effectiveTo: membership.activeUntil
    }))
    const useTargetAggregation = scope?.useTargetCreditedMetricAggregation === true
    const coverage = useTargetAggregation
      ? await this.flowExecutor.getCreditedMetricRuleCoverage({
          creditSetupId: metricRef.credit_setup_id,
          profileId,
          teamMemberships: memberships.filter(membership => membership.profileId === profileId),
          startDate: periodStart,
          endDate: periodEnd
        })
      : null
    const metric = await this.flowExecutor.getMetricByIdForExecution(metricRef.id)
    const aggregatedValue = useTargetAggregation
      ? ((
          await this.flowExecutor.rpcClient.getCreditedTargetMetricValueAggregates([
            {
              organizationId: this.flowExecutor.organizationId,
              metricId: metricRef.id,
              creditSetupId: metricRef.credit_setup_id,
              profileId,
              teamIds:
                scope?.creditedTeamIds ?? teamMemberships.map(membership => membership.teamId),
              teamMemberships,
              targetCurrency: scope?.targetCurrency,
              aggregateFunction: metric?.aggregate_function ?? 'sum',
              ruleSegments: coverage!.coverage.flatMap(segment =>
                segment.resolution
                  ? [
                      {
                        creditSetupRuleId: segment.resolution.ruleId,
                        effectiveFrom: segment.effectiveFrom,
                        effectiveTo: segment.effectiveTo
                      }
                    ]
                  : []
              ),
              startDate: periodStart,
              endDate: periodEnd
            }
          ])
        )[0]?.value ?? 0)
      : undefined
    const rows = useTargetAggregation
      ? []
      : await this.flowExecutor.getCreditedMetricValueRows({
          organizationId: this.flowExecutor.organizationId,
          metricId: metricRef.id,
          creditSetupId: metricRef.credit_setup_id,
          profileId,
          teamIds: scope?.creditedTeamIds ?? teamMemberships.map(membership => membership.teamId),
          teamMemberships,
          // The beneficiary resolves the Rule; source ownership does not limit its values.
          valueProfileIds: null,
          sourceIdLimitOnLastDay: sourceId,
          targetCurrency: scope?.targetCurrency,
          startDate: periodStart,
          endDate: periodEnd
        })

    if (useTargetAggregation) {
      const value = aggregatedValue ?? 0
      captureMetric(value)
      return value
    }
    if (rows.length === 0) {
      captureMetric(0)
      return 0
    }

    const value = aggregateCreditedMetricValues(
      rows.map(row => ({
        id: row.metric_value_id,
        accrual_date: row.accrual_date,
        value: row.value
      })),
      metric?.aggregate_function ?? 'sum'
    )
    captureMetric(value)
    return value
  }

  private capturePeriodMetricInput(
    metric: MetricRef,
    value: number,
    periodStart?: string,
    periodEnd?: string,
    currency?: string
  ) {
    if (!this.flowExecutor.period) return

    this.flowExecutor.execution.captureCalculationInput({
      kind: 'metric',
      nodeId: this.node.id,
      metric: { ...metric },
      value,
      attainment: value,
      period: {
        start: periodStart ?? this.flowExecutor.period.start,
        end: periodEnd ?? this.flowExecutor.period.end
      },
      ...(currency ? { currency } : {}),
      ...(metric.aggregate_function === 'count' ? { count: value } : {})
    })
  }

  public getTargetData(filters: Record<string, unknown>) {
    const evaluator = new LogicEvaluator(this.flowExecutor.source, {})
    return evaluator.getTargetData(filters)
  }

  protected async loadTargetAchievements(
    targetData: { id: number; period?: Enums<'period'>; fullPeriod?: boolean }[]
  ) {
    for (const { id: targetId, period: targetPeriod, fullPeriod } of targetData) {
      const key = [
        `target_${targetId}`,
        targetPeriod,
        !this.flowExecutor.period && fullPeriod ? 'full_period' : undefined
      ]
        .filter(Boolean)
        .join('_')

      const startDate =
        !!this.flowExecutor.period && targetPeriod
          ? getPeriodStartEnd(targetPeriod, new Date(this.flowExecutor.period.start))!.start
          : targetPeriod && this.getRecordProcessingDate()
            ? getPeriodStartEnd(targetPeriod, new Date(this.getRecordProcessingDate()!))!.start
            : undefined
      const endDate =
        !this.flowExecutor.period && targetPeriod && this.getRecordProcessingDate()
          ? getPeriodStartEnd(targetPeriod, new Date(this.getRecordProcessingDate()!))!.end
          : undefined

      this.targetAchievements[key] ??=
        ((
          await this.getPeriodTargetAchievement(
            targetId,
            startDate,
            endDate,
            !this.flowExecutor.period ? fullPeriod : undefined
          )
        )?.achievement ?? 0) * 100
    }
  }

  protected async loadWeightedTargetAchievement(
    targets?: { id: number; weight?: number }[],
    useTargetWeights?: boolean
  ) {
    this.weightedTargetAchievement = 0

    const configuredTargets =
      targets?.filter(target => Number.isInteger(target.id) && target.id > 0) ?? []

    if (!useTargetWeights || configuredTargets.length < 2) {
      return
    }

    for (const target of configuredTargets) {
      const achievement = await this.getPeriodTargetAchievement(target.id)
      this.weightedTargetAchievement +=
        (achievement?.achievement ?? 0) *
        (typeof target.weight === 'number' && Number.isFinite(target.weight) ? target.weight : 0)
    }

    this.weightedTargetAchievement *= 100
  }

  protected async loadMetricAndTargetValues(filters: Record<string, unknown>) {
    if (!!this.flowExecutor.period) {
      const metricIds = this.getMetricIds(filters)
      await this.loadMetricValues(metricIds)
    }

    const targetData = this.getTargetData(filters)
    await this.loadTargetAchievements(targetData)
  }

  protected parseCompensationValues(execution?: DolfinExecution) {
    const compensationValues =
      execution?.getVariable<{ profile_id: string; value: number }[]>('compensation_values')
    const compensationValuesPeriodCandidates = execution?.getVariable<
      { profile_id: string; value: number }[]
    >('compensation_values_period_candidates')
    const compensationValuePeriodCandidate = execution?.getVariable<number>(
      'compensation_value_period_candidate'
    )

    if (compensationValues) {
      ;(this.node.data as TypedNodeData<Exclude<TNodeId, 'placeholder'>>).parsedValues = {
        compensationValues,
        ...(compensationValuesPeriodCandidates ? { compensationValuesPeriodCandidates } : {})
      }
    } else {
      ;(this.node.data as TypedNodeData<Exclude<TNodeId, 'placeholder'>>).parsedValues = {
        compensationValue: execution?.getVariable<number>('compensation_value') || 0,
        ...(compensationValuePeriodCandidate !== undefined
          ? { compensationValuePeriodCandidate }
          : {})
      }
    }
  }

  protected getRecordProcessingDate() {
    return this.flowExecutor.recordData!.getProcessingDate(
      this.flowExecutor.dateField,
      this.flowExecutor.customFields
    )
  }

  protected getRecordProfileId() {
    return this.flowExecutor.recordData!.getFieldValue(
      this.flowExecutor.userField,
      this.flowExecutor.customFields
    ) as string | null
  }

  protected async getPeriodTargetAchievement(
    targetId: number,
    periodStart?: string,
    periodEnd?: string,
    forceFullPeriod?: boolean,
    sourceIdLimitOnLastDay?: number
  ) {
    const processingDate = !this.flowExecutor.period ? this.getRecordProcessingDate() : undefined
    if (!this.flowExecutor.period && !processingDate) {
      this.logger.error('Processing date not found')
      return {
        achievement: 0,
        target_amount: 0
      }
    }

    const target = this.flowExecutor.period
      ? await this.flowExecutor.rpcClient.getTarget(targetId)
      : null

    if (
      this.flowExecutor.period &&
      (!target || typeof target.label !== 'string' || target.label.trim().length === 0)
    ) {
      throw new Error(`Target ${targetId} is missing the label required for calculation context`)
    }

    if (this.flowExecutor.period) {
      if (target?.credit_setup_id != null && target.metrics) {
        const start = periodStart ?? this.flowExecutor.period.start
        const end = periodEnd ?? this.flowExecutor.period.end
        const profileId = this.flowExecutor.profileId!
        const scope = await this.flowExecutor.getTargetCreditedMetricScope(targetId, profileId, end)

        if (!scope) {
          return {
            achievement: 0,
            target_amount: 0
          }
        }

        const [targetAmount, totalValue] = await Promise.all([
          this.flowExecutor.rpcClient.getPeriodTargetAmount(targetId, start, end, profileId),
          this.getMetricReferenceValue(
            {
              id: target.metrics.id,
              credit_setup_id: target.credit_setup_id
            },
            start,
            end,
            undefined,
            scope
          )
        ])
        const amount = Number(targetAmount.total_amount ?? 0)

        return {
          achievement: Targets.computeTargetAchievement(
            amount,
            target.is_upper_bound,
            Boolean(target.allow_partial_outcome),
            totalValue
          ),
          target_amount: amount
        }
      }
    }

    const achievement = this.flowExecutor.period
      ? await this.flowExecutor.rpcClient.getPeriodTargetAchievement(
          targetId,
          periodStart ?? this.flowExecutor.period.start,
          this.flowExecutor.period.end,
          this.flowExecutor.profileId!
        )
      : await this.flowExecutor.rpcClient.getPeriodTargetAchievement(
          targetId,
          periodStart ?? '1970-01-01',
          periodEnd ?? '1970-01-01',
          this.getRecordProfileId()!,
          !periodStart || !periodEnd ? processingDate! : undefined,
          sourceIdLimitOnLastDay ??
            (!forceFullPeriod ? this.flowExecutor.recordData!.record.id : undefined)
        )

    if (!achievement) {
      this.logger.error(
        `Target achievement not found for targetId: ${targetId}, periodStart: ${periodStart}, processingDate: ${processingDate}`
      )
      return {
        achievement: 0,
        target_amount: 0
      }
    }

    const result = {
      achievement: achievement.achievement ?? 0,
      target_amount: achievement.target_amount ?? 0
    }
    const teamIds = achievement.team_ids ?? []

    if (this.flowExecutor.period && target) {
      this.flowExecutor.execution.captureCalculationInput({
        kind: 'target',
        nodeId: this.node.id,
        target: {
          id: targetId,
          label: target.label,
          team_target: achievement.team_target ?? false,
          ...(achievement.global_target ? { global_target: true } : {}),
          ...(achievement.global_target && teamIds.length > 0
            ? { teams_targets: teamIds.map(team_id => ({ team_id })) }
            : {})
        } satisfies TargetRef,
        value: result.achievement * result.target_amount,
        attainment: result.achievement,
        targetAmount: result.target_amount,
        period: {
          start: periodStart ?? this.flowExecutor.period.start,
          end: this.flowExecutor.period.end
        }
      })
    }

    return result
  }

  abstract process(): Promise<NodeProcessOutput<TNodeId>>

  async parse(
    _diagram: Diagram,

    _execution?: DolfinExecution
  ): Promise<TypedNode<TNodeId>> {
    return this.node
  }
}
